import _ from 'lodash';
import murmurhash from 'murmurhash-js';

import { TopicsMenuOptionType } from 'components/customer/conversation_history/topics_v2/conversation_topics_constants_v2';

/**
 * Build the displayed option list for the topics multiselect: applies the
 * search filter, layers in selected / suggested / unselected groupings,
 * and (with `enhancedTopicWorkflows`) buckets unselected topics under their
 * category-only parents.
 *
 * Pure aside from `collapseSelections`, which the caller supplies so the
 * stateful collapse/expand decision (driven by component state) stays in
 * the owning component.
 */
export default function filterTopicOptions(
  options,
  filter,
  selectedTopicIds,
  selectedAttributeValues,
  { suggestedLabel, unselectedLabel, enhancedTopicWorkflows, collapseSelections }
) {
  const selectedIds = {};
  _.forEach(selectedTopicIds, id => {
    selectedIds[id] = true;
  });
  const mappedOptions = _.map(options, o => ({
    ...o,
    selected: !!selectedIds[o.id],
    wasSelected: !!selectedIds[o.id] || o.wasSelected,
  }));

  const filterLower = filter.toLowerCase();
  const matchesFilter = o => filter === '' || o.selected || o.label.toLowerCase().indexOf(filterLower) !== -1;

  // Keep category-only topics if any of their children match the filter
  const matchingChildParentIds =
    filter === ''
      ? new Set()
      : new Set(
          _(mappedOptions)
            .filter(o => !o.categoryOnly && o.parentId && matchesFilter(o))
            .map('parentId')
            .value()
        );

  const filteredOptions = _(mappedOptions)
    .filter(o => matchesFilter(o) || (o.categoryOnly && matchingChildParentIds.has(o.id)))
    .sortBy(o => o.label.toLowerCase())
    .value();

  const selectedOptions = _.filter(filteredOptions, o => o.selected);
  let suggestedOptions = _.filter(
    filteredOptions,
    o => o.suggested && !o.selected && !o.wasSelected && !o.categoryOnly
  );
  suggestedOptions = _.map(suggestedOptions, o => ({
    ...o,
    optionId: `${o.optionId}-suggested`,
  }));
  let unselectedOptions = _.filter(filteredOptions, o => !o.selected).map(o => ({ ...o, suggested: false }));
  unselectedOptions = _.filter(unselectedOptions, o => !o.archived);

  for (let i = 0; i < selectedAttributeValues.length; i++) {
    const attr = selectedAttributeValues[i];
    const option = _.find(options, t => t.id === attr.id);
    if (!option) {
      continue;
    }

    const hashValue = murmurhash.murmur3(`${option.id}-${attr.value}`).toString();
    selectedOptions.push({
      ...option,
      optionId: hashValue,
      selected: true,
      wasSelected: true,
      label: `${option.label} > ${attr.value} ${option.archived ? ' (Archived)' : ''}`,
      optionValue: attr.value,
    });
  }

  let currentOptions = collapseSelections(selectedOptions);
  /* add divider between selected and unselected options */
  if (selectedOptions.length > 0 && unselectedOptions.length > 0) {
    currentOptions.push({
      disabled: true,
      type: TopicsMenuOptionType.DIVIDER,
      unfocusable: true,
      optionId: TopicsMenuOptionType.DIVIDER,
    });
  }

  if (suggestedOptions.length > 0 && suggestedLabel) {
    currentOptions.push({
      label: suggestedLabel,
      disabled: true,
      type: TopicsMenuOptionType.GROUP_TITLE,
      unfocusable: true,
      optionId: 'suggested-topics-title',
    });
    currentOptions = _.concat(currentOptions, suggestedOptions);
  }

  // Separate category-only topics from regular unselected options
  const categoryOnlyTopics = enhancedTopicWorkflows ? _.filter(unselectedOptions, o => o.categoryOnly) : [];
  const categoryOnlyIds = new Set(_.map(categoryOnlyTopics, 'id'));

  if (categoryOnlyIds.size > 0) {
    const groupedChildren = _.filter(unselectedOptions, o => !o.categoryOnly && categoryOnlyIds.has(o.parentId));
    const ungroupedOptions = _.filter(unselectedOptions, o => !o.categoryOnly && !categoryOnlyIds.has(o.parentId));
    const childrenByParent = _.groupBy(groupedChildren, 'parentId');

    if (ungroupedOptions.length > 0 && unselectedLabel) {
      currentOptions.push({
        label: unselectedLabel,
        disabled: true,
        type: TopicsMenuOptionType.GROUP_TITLE,
        unfocusable: true,
        optionId: 'all-topics-title',
      });
    }
    currentOptions = _.concat(currentOptions, ungroupedOptions);

    _.forEach(categoryOnlyTopics, category => {
      const children = childrenByParent[category.id] || [];
      if (children.length > 0) {
        currentOptions.push({
          label: category.label,
          disabled: true,
          type: TopicsMenuOptionType.GROUP_TITLE,
          unfocusable: true,
          optionId: `category-title-${category.id}`,
        });
        const childOptions = _.map(children, child => ({
          ...child,
          label: child.label.replace(`${category.label} > `, ''),
        }));
        currentOptions = _.concat(currentOptions, childOptions);
      }
    });
  } else {
    if (unselectedOptions.length > 0 && unselectedLabel) {
      currentOptions.push({
        label: unselectedLabel,
        disabled: true,
        type: TopicsMenuOptionType.GROUP_TITLE,
        unfocusable: true,
        optionId: 'all-topics-title',
      });
    }
    currentOptions = _.concat(currentOptions, unselectedOptions);
  }

  return currentOptions;
}
